import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import { PaginatedResponse, UserPlan, UserPlansQueryParams } from '../types/api';
import { appendQueryString } from '../utils/queryParams';

type RawUserPlan = Record<string, unknown>;

const toStringValue = (value: unknown, fallback = ''): string =>
  value === undefined || value === null ? fallback : String(value);

const toNumberValue = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toOptionalString = (value: unknown): string | null | undefined => {
  if (value === undefined || value === null) return undefined;
  const str = String(value).trim();
  return str || null;
};

/** Map legacy camelCase API rows or normalized snake_case rows to UserPlan. */
export const normalizeUserPlan = (raw: RawUserPlan): UserPlan => {
  if (raw.first_name !== undefined || raw.last_name !== undefined) {
    return {
      user_plan_id: toOptionalNumber(raw.user_plan_id ?? raw.userPlanId),
      user_id: toStringValue(raw.user_id),
      unique_id: (raw.unique_id as string | null | undefined) ?? null,
      first_name: toStringValue(raw.first_name),
      last_name: toStringValue(raw.last_name),
      planId: toStringValue(raw.planId),
      name: toStringValue(raw.name),
      duration_months: toNumberValue(raw.duration_months),
      start_date: toStringValue(raw.start_date),
      end_date: toStringValue(raw.end_date),
      is_active: Boolean(raw.is_active),
      base_price: toStringValue(raw.base_price, '0'),
      tax_price: toStringValue(raw.tax_price, '0'),
      total_price: toStringValue(raw.total_price, '0'),
      currency: toStringValue(raw.currency, 'INR'),
      connects: toNumberValue(raw.connects),
      connects_used: toNumberValue(raw.connects_used),
      note_from_admin: toOptionalString(raw.note_from_admin ?? raw.noteFromAdmin),
    };
  }

  const clientName = toStringValue(raw.clientName);
  const nameParts = clientName.split(/\s+/).filter(Boolean);

  return {
      user_plan_id: toOptionalNumber(raw.user_plan_id ?? raw.userPlanId),
    user_id: toStringValue(raw.userId ?? raw.user_id),
    unique_id: (raw.uniqueId as string | null | undefined) ?? null,
    first_name: nameParts[0] ?? '',
    last_name: nameParts.slice(1).join(' '),
    planId: toStringValue(raw.planId ?? raw.userPlanId ?? raw.user_plan_id),
    name: toStringValue(raw.planName ?? raw.name),
    duration_months: toNumberValue(raw.durationMonths ?? raw.duration_months),
    start_date: toStringValue(raw.startDate ?? raw.start_date ?? raw.date),
    end_date: toStringValue(raw.endDate ?? raw.end_date),
    is_active:
      raw.is_active !== undefined
        ? Boolean(raw.is_active)
        : raw.status === 'active',
    base_price: toStringValue(raw.base_price, '0'),
    tax_price: toStringValue(raw.tax_price, '0'),
    total_price: toStringValue(raw.total_price ?? raw.amount, '0'),
    currency: toStringValue(raw.currency, 'INR'),
    connects: toNumberValue(raw.connects),
    connects_used: toNumberValue(raw.connects_used),
    note_from_admin: toOptionalString(raw.note_from_admin ?? raw.noteFromAdmin),
  };
};

/**
 * User Plan Service - Admin User Plans Management
 */
export class UserPlanService extends ApiService {
  /**
   * Get all user plans with pagination
   * GET /admin/user/all-plans
   */
  async getUserPlans(params?: UserPlansQueryParams): Promise<PaginatedResponse<UserPlan>> {
    const url = appendQueryString(API_ENDPOINTS.userPlans.list, {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      status: params?.status,
      planId: params?.planId,
      timePeriod: params?.timePeriod && params.timePeriod !== 'all_time'
        ? params.timePeriod
        : undefined,
      durationMonths: params?.durationMonths,
    });

    const response = await this.get<PaginatedResponse<RawUserPlan> | RawUserPlan[]>(url);

    if (Array.isArray(response)) {
      const data = response.map(normalizeUserPlan);
      return {
        data,
        metaData: {
          page: params?.page ?? 1,
          limit: params?.limit ?? (data.length || 10),
          total: data.length,
          totalPages: data.length > 0 ? 1 : 0,
        },
      };
    }

    return {
      ...response,
      data: (response.data ?? []).map(normalizeUserPlan),
    };
  }
}

// Export singleton instance
export const userPlanService = new UserPlanService();
